import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import {
  useAdminScope,
} from "../../../context/AdminScopeContext";

import {
  EMPLOYEE_DETAIL_STATUS,
  createEmployeeDetailLoadingState,
  loadEmployeeDetail,
} from "../../../services/employeeDetailLoader";

import {
  AdminBackButton,
} from "../navigation_engine";

import {
  AdminSection,
} from "../section_engine";

import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../ui_engine";

import EmployeeAdminActionsPanel, {
  EMPTY_EMPLOYEE_ACTION_AVAILABILITY,
} from "./EmployeeAdminActionsPanel";

import EmployeeIdentitySummary
  from "./EmployeeIdentitySummary";

import EmployeeWorkspaceSections
  from "./EmployeeWorkspaceSections";


function unexpectedErrorState(error) {
  const message =
    typeof error?.message === "string"
    && error.message.trim()
      ? error.message.trim()
      : "Impossible de charger la fiche salarié.";

  return {
    status:
      EMPLOYEE_DETAIL_STATUS.GENERIC_ERROR,
    data: null,
    error: message,
  };
}


export default function EmployeeWorkspaceShell({
  employeeUuid,
  onBack,
  navigation = null,
  children = null,
  extensions = null,
  actionAvailability =
    EMPTY_EMPLOYEE_ACTION_AVAILABILITY,
  confirmAction = null,
  promptAction = null,
}) {
  const {
    effectiveCompanyUuid,
  } = useAdminScope();

  const [
    refreshVersion,
    setRefreshVersion,
  ] = useState(0);

  const [
    detailState,
    setDetailState,
  ] = useState(
    createEmployeeDetailLoadingState,
  );

  const requestIdRef =
    useRef(0);

  const workspaceRootRef =
    useRef(null);

  const focusedEmployeeRef =
    useRef(null);


  useEffect(() => {
    const requestId =
      requestIdRef.current + 1;

    requestIdRef.current =
      requestId;

    let active = true;

    setDetailState(
      createEmployeeDetailLoadingState(),
    );

    Promise.resolve(
      loadEmployeeDetail({
        employeeUuid,
        companyUuid:
          effectiveCompanyUuid,
      }),
    )
      .then((nextState) => {
        if (
          !active
          || requestId
            !== requestIdRef.current
        ) {
          return;
        }

        setDetailState(
          nextState,
        );
      })
      .catch((error) => {
        if (
          !active
          || requestId
            !== requestIdRef.current
        ) {
          return;
        }

        setDetailState(
          unexpectedErrorState(
            error,
          ),
        );
      });

    return () => {
      active = false;

      if (
        requestId
        === requestIdRef.current
      ) {
        requestIdRef.current += 1;
      }
    };
  }, [
    employeeUuid,
    effectiveCompanyUuid,
    refreshVersion,
  ]);


  useEffect(() => {
    if (
      detailState.status
      !== EMPLOYEE_DETAIL_STATUS.LOADED
    ) {
      return;
    }

    if (
      focusedEmployeeRef.current
      === employeeUuid
    ) {
      return;
    }

    const heading =
      workspaceRootRef.current
        ?.querySelector(
          "#employee-identity-summary-title",
        );

    if (!heading) {
      return;
    }

    heading.focus({
      preventScroll: true,
    });

    focusedEmployeeRef.current =
      employeeUuid;
  }, [
    detailState.status,
    employeeUuid,
  ]);


  const refreshEmployeeDetail = async () => {
    setRefreshVersion(
      (current) => current + 1,
    );
  };


  const renderState = () => {
    switch (detailState.status) {
      case EMPLOYEE_DETAIL_STATUS.LOADING:
        return (
          <AdminLoadingState
            message={
              "Chargement de la fiche salarié..."
            }
          />
        );

      case EMPLOYEE_DETAIL_STATUS.NOT_FOUND:
        return (
          <AdminEmptyState
            message={
              detailState.error
              || "Salarié introuvable."
            }
          />
        );

      case EMPLOYEE_DETAIL_STATUS.FORBIDDEN:
        return (
          <AdminErrorState
            message={
              detailState.error
              || "Accès à cette fiche salarié interdit."
            }
          />
        );

      case EMPLOYEE_DETAIL_STATUS.INTEGRITY_ERROR:
        return (
          <AdminErrorState
            message={
              detailState.error
              || "La fiche salarié présente un conflit d’intégrité."
            }
          />
        );

      case EMPLOYEE_DETAIL_STATUS.GENERIC_ERROR:
        return (
          <AdminErrorState
            message={
              detailState.error
              || "Impossible de charger la fiche salarié."
            }
          />
        );

      case EMPLOYEE_DETAIL_STATUS.LOADED:
        return (
          <LoadedWorkspace>
            <EmployeeIdentitySummary
              detail={detailState.data}
            />

            <EmployeeAdminActionsPanel
              employee={
                detailState.data?.employee
              }
              user={
                detailState.data?.user
              }
              actionAvailability={
                actionAvailability
              }
              confirmAction={
                confirmAction
              }
              promptAction={
                promptAction
              }
              onEmployeeChanged={
                refreshEmployeeDetail
              }
            />

            <EmployeeWorkspaceSections
              detail={detailState.data}
            />

            {navigation ? (
              <WorkspaceNavigation
                aria-label={
                  "Navigation Employee Workspace"
                }
              >
                {navigation}
              </WorkspaceNavigation>
            ) : null}

            <WorkspaceContent
              aria-label={
                "Contenu Employee Workspace"
              }
            >
              {children}
            </WorkspaceContent>

            {extensions ? (
              <WorkspaceExtensions
                aria-label={
                  "Extensions Employee Workspace"
                }
              >
                {extensions}
              </WorkspaceExtensions>
            ) : null}
          </LoadedWorkspace>
        );

      default:
        return (
          <AdminErrorState
            message={
              "État Employee Detail inconnu."
            }
          />
        );
    }
  };


  return (
    <WorkspaceRoot
      ref={workspaceRootRef}
      aria-label="Employee Workspace"
      data-employee-uuid={
        employeeUuid
      }
      data-testid={
        "employee-workspace-route"
      }
    >
      <WorkspaceToolbar>
        <AdminBackButton
          type="button"
          onClick={onBack}
        >
          Retour à l’annuaire
        </AdminBackButton>
      </WorkspaceToolbar>

      <AdminSection
        $compact
        aria-label={
          "État Employee Workspace"
        }
      >
        {renderState()}
      </AdminSection>
    </WorkspaceRoot>
  );
}


const WorkspaceRoot = styled.main`
  display: grid;
  gap: 1rem;

  min-width: 0;
  width: 100%;
`;


const WorkspaceToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  min-width: 0;
`;


const LoadedWorkspace = styled.div`
  display: grid;
  gap: 1rem;

  min-width: 0;
`;


const WorkspaceNavigation = styled.nav`
  min-width: 0;
`;


const WorkspaceContent = styled.div`
  min-width: 0;
`;


const WorkspaceExtensions = styled.div`
  min-width: 0;
`;
